// src/components/PickBreakdownPanel.tsx
import React, { useState } from 'react';
import { GameBreakdown } from '../utils/pickBreakdownUtils';
import { X, ChevronDown, ChevronRight, Pin, PinOff, CircleCheck } from 'lucide-react';
import { cn } from '../lib/utils';
import { useConfig } from '../context/ConfigContext';
import { usePinnedPlayers } from '../hooks/usePinnedPlayers';
import { Badge } from './ui/badge';

interface PickBreakdownPanelProps {
  breakdown: GameBreakdown;
  pinnedPlayers: string[];
  onPinnedPlayersChange?: (pinnedPlayers: string[]) => void;
  getTeamSeed: (teamName: string) => number;
  onClose: () => void;
  /** When true, renders as a mobile bottom-sheet with backdrop. Otherwise renders inline. */
  mobile?: boolean;
}

const PickBreakdownPanelContent: React.FC<Omit<PickBreakdownPanelProps, 'mobile'>> = ({
  breakdown,
  pinnedPlayers,
  onPinnedPlayersChange,
  getTeamSeed,
  onClose,
}) => {
  const { roundConfigs } = useConfig();
  const { togglePin, pinToast, MAX_PINS } = usePinnedPlayers(pinnedPlayers, onPinnedPlayersChange);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());

  const toggleTeam = (team: string) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(team)) next.delete(team);
      else next.add(team);
      return next;
    });
  };

  const roundNames = [
    'Round of 64',
    roundConfigs[0]?.name || 'Round of 32',
    roundConfigs[1]?.name || 'Sweet 16',
    roundConfigs[2]?.name || 'Elite 8',
    roundConfigs[3]?.name || 'Final Four',
    roundConfigs[4]?.name || 'Championship',
  ];

  const roundName = roundNames[breakdown.round] || `Round ${breakdown.round}`;

  return (
    <>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b px-4 py-3 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">
            {roundName}
          </h3>
          <p className="text-xs text-muted-foreground">
            {breakdown.region}
            {breakdown.isOfficial && ' (Completed)'}
            {' — '}
            {breakdown.totalEntries} {breakdown.totalEntries === 1 ? 'entry' : 'entries'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Pinned players summary */}
      {(() => {
        // Build a map of pinned player → team they picked for this game
        const pinnedEntries: { name: string; team: string | null }[] = [];
        for (const name of pinnedPlayers) {
          let foundTeam: string | null = null;
          for (const pick of breakdown.picks) {
            if (pick.entries.includes(name)) {
              foundTeam = pick.team;
              break;
            }
          }
          // Only show pinned players that are in this pool
          if (foundTeam !== null) {
            pinnedEntries.push({ name, team: foundTeam });
          }
        }
        if (pinnedEntries.length === 0) return null;
        return (
          <div className="px-4 py-2 border-b space-y-1">
            {pinnedEntries.map(({ name, team }) => (
              <div key={name} className="flex items-center gap-1.5 text-xs">
                <button
                  onClick={() => togglePin(name)}
                  className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
                  title="Unpin entry"
                >
                  <Pin className="h-3 w-3 text-warning fill-warning" />
                </button>
                <span className="font-medium">{name}</span>
                <span className="text-muted-foreground">—</span>
                <span className="text-muted-foreground">{team}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Team list */}
      <div className="p-2">
        {breakdown.picks.length === 0 ? (
          <div className="text-sm text-muted-foreground italic p-4 text-center">
            No picks for this game
          </div>
        ) : (
          breakdown.picks.map(pick => {
            const isExpanded = expandedTeams.has(pick.team);
            const pinnedInThisTeam = pick.entries.filter(e => pinnedPlayers.includes(e));
            const unpinnedEntries = pick.entries.filter(e => !pinnedPlayers.includes(e));
            const isOfficialWinner = breakdown.isOfficial && pick.team === breakdown.officialWinner;

            return (
              <div key={pick.team} className="mb-1">
                {/* Team header row */}
                <button
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors text-left",
                    isOfficialWinner && "bg-teal/5"
                  )}
                  onClick={() => toggleTeam(pick.team)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}

                  {isOfficialWinner && (
                    <CircleCheck className="h-3 w-3 shrink-0 text-teal" />
                  )}

                  <Badge variant="secondary" className="justify-center rounded-sm py-0 min-w-[22px] px-1 text-xs">
                    {getTeamSeed(pick.team)}
                  </Badge>

                  <span className="font-medium flex-1 truncate">{pick.team}</span>

                  <span className="tabular-nums text-muted-foreground shrink-0 text-xs">
                    {pick.count}
                  </span>

                  {/* Percentage bar */}
                  <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
                    <div
                      className={cn(
                        "h-full rounded-full",
                        isOfficialWinner ? "bg-teal" : "bg-primary"
                      )}
                      style={{ width: `${pick.percentage}%` }}
                    />
                  </div>

                  <span className="tabular-nums text-xs text-muted-foreground w-8 text-right shrink-0">
                    {pick.percentage}%
                  </span>
                </button>

                {/* Expanded entries */}
                {isExpanded && (
                  <div className="ml-8 pl-2 border-l border-border/50 py-1 space-y-0.5">
                    {pinnedInThisTeam.map(entry => (
                      <div key={entry} className="flex items-center gap-1 px-2 py-0.5 text-xs">
                        <button
                          onClick={() => togglePin(entry)}
                          className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
                          title="Unpin entry"
                        >
                          <Pin className="h-3 w-3 text-warning fill-warning" />
                        </button>
                        <span className="font-medium">{entry}</span>
                      </div>
                    ))}
                    {unpinnedEntries.map(entry => (
                      <div key={entry} className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted-foreground">
                        <button
                          onClick={() => togglePin(entry)}
                          className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
                          title="Pin entry"
                        >
                          <PinOff className="h-3 w-3 text-muted-foreground" />
                        </button>
                        <span>{entry}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Max pins toast */}
      {pinToast && (
        <div className="sticky bottom-0 mx-2 mb-2 px-3 py-2 rounded-md bg-destructive/10 text-destructive text-xs text-center">
          Maximum {MAX_PINS} pinned entries reached
        </div>
      )}
    </>
  );
};

const PickBreakdownPanel: React.FC<PickBreakdownPanelProps> = (props) => {
  const { mobile = false } = props;

  if (mobile) {
    return (
      <>
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={props.onClose}
        />
        <div className="fixed inset-x-0 bottom-0 z-50 bg-card border shadow-xl overflow-y-auto max-h-[70vh] rounded-t-xl">
          <PickBreakdownPanelContent {...props} />
        </div>
      </>
    );
  }

  // Desktop: render inline (parent handles placement in sidebar)
  return <PickBreakdownPanelContent {...props} />;
};

export default PickBreakdownPanel;
